import { z } from "zod";

/**
 * Common building-block schemas used across the per-aggregate
 * routers. Mirrors the Phase 6a codec's hand-written validators (the
 * domain core is Zod-free; contracts re-validates at the boundary).
 */

/**
 * ISO 8601 instant with millisecond precision. The codec uses
 * `canonicalInstant` to produce this shape; the schema rejects empty
 * strings and obvious garbage. Full RFC 3339 parsing happens
 * downstream at the codec — this is the network-boundary smoke test.
 */
export const instantSchema = z
  .string()
  .min(1)
  .regex(
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/,
    "Instant must be canonical ISO 8601 UTC with millisecond precision " +
      "(e.g., 2026-05-01T12:00:00.000Z).",
  );

/** Non-empty stable identifier — domain ids, tokens, owner ids. */
export const idSchema = z.string().min(1).max(256);

/**
 * Idempotency key — client-supplied UUID-shaped string. The middleware
 * stores this against the request's response hash for 24h; a retry
 * with the same key returns the cached response.
 *
 * `MVP.md` §4.9 / `Architecture.md` §3: every mutating procedure
 * accepts one of these. We accept any non-empty 1–128 ASCII string
 * (not strictly UUID-shaped) so a Phase 13 client that wants a
 * structured key (e.g., `panel-create:{deviceId}:{lamportLogical}`)
 * can use one.
 */
export const idempotencyKeySchema = z
  .string()
  .min(1)
  .max(128)
  .regex(/^[A-Za-z0-9_:.\-]+$/, "Idempotency key must be ASCII alphanumeric " +
    "plus '_:.-' (URL-safe, log-safe).");

/**
 * Lite-tier label-sheet codename (set, not arbitrary string) for the
 * Phase 11b legend renderer geometry argument. Mirrors
 * `LABEL_SHEET_GEOMETRY_REGISTRY` keys.
 */
export const labelSheetGeometrySchema = z.enum([
  "squareD-homeline",
  "siemens",
  "generic-letter-a4",
]);

/**
 * Page size for PDF rendering. Defaults to `letter` per `MVP.md` §7.
 */
export const pageSizeSchema = z.enum(["letter", "a4"]);
